import React from 'react';
import { useSelector } from 'react-redux';

// styles
import { BannerWrapper } from '../../routing/routes.style';

// libraries
import * as multiScenarioSelectors from '@makemydeal/dr-dash-store/selectors/multiScenarioSelectors';
import { useDashComponentConfig } from '@makemydeal/dr-dash-store/hooks/useDashboardComponentConfig';
import type { InitConfigurationStaticAssetsState } from '@makemydeal/dr-dash-types';
import {
    featureToggleReducer,
    featureToggleSelectors,
    dealerSelectors,
    type FeatureTogglesAndUrlOnly,
    sharedBootstrapSelectors
} from '@makemydeal/dr-shared-store';
import type { Dealer } from '@makemydeal/dr-shared-types';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import { ScopedStateProvider } from '@makemydeal/shared-scoped-store';
import { selectors } from '@makemydeal/dr-activities-open-deal-v2';

// config
import { accessoriesCatalogConfiguration } from '../../activities/accessories-catalog';
import { changeVehicleConfiguration } from '../../activities/change-vehicle';
import { creditBureauConfiguration } from '../../activities/credit-bureau';
import { creditDecisionConfiguration } from '../../activities/credit-decision';
import { dealHistoryConfiguration } from '../../activities/deal-history';
import { deskingConfiguration } from '../../activities/desking';
import { tradeConfiguration, isTradeConfigured } from '../../activities/trade';
import { ddjConfiguration, DDJPreloader } from '../../activities/digital-deal-jacket';
import { auditLogConfiguration } from '../../activities/audit-log';

// components
import {
    EmployeeDetailSlideOut,
    CreditAppSearchSlideOut,
    CreditAppSearchSlideOutV2,
    GenerateContract,
    UnsavedChangesManager,
    UpdatedDealBanner,
    Banner,
    Modal,
    OutOfBandUpdatedDealBanner,
    PushToDMSSuccessBanner,
    Footer,
    PushToDarwinPopup,
    DealHistoryScenarios,
    NavigationDrawerInterstate,
    ConnectionStatusBanner,
    RepairOrdersSlideOut,
    PendingReviewRetentionHost
} from '@makemydeal/dr-dash-components';
import { BodyContainer } from '../../routing/BodyContainer';
import { DealershipSelectionModal } from '../dealership-selection/DealershipSelectionModal';
import { CreateDealModal } from '../create-deal/CreateDealModal';
import { NotificationContainer } from '../notification/NotificationContainer';
import { DealHistoryDrawerContainer } from '../deal-history-drawer/DealHistoryDrawerContainer';
import { DealTypeOverrideBanner } from './DealTypeOverrideBanner';
import { FuelSavingContainer } from '../../activities/fuel-saving';

export type RoutesStateProps = {
    toggles: FeatureTogglesAndUrlOnly;
    staticAssets: InitConfigurationStaticAssetsState | undefined;
    dealer: Partial<Dealer>;
};

const configs = {
    changeVehicle: changeVehicleConfiguration,
    creditBureau: creditBureauConfiguration,
    dealHistory: dealHistoryConfiguration,
    desking: deskingConfiguration,
    trade: tradeConfiguration,
    accessoriesCatalog: accessoriesCatalogConfiguration,
    creditDecision: creditDecisionConfiguration,
    ddj: ddjConfiguration,
    auditLog: auditLogConfiguration
};

export const Dashboard: React.FC<RoutesStateProps> = ({ toggles, dealer }) => {
    const isBootstrapped = useSelector(sharedBootstrapSelectors.wasBootstrapSuccessful);
    // isTradeConfigured() is the real precondition; the redux flag is what re-renders us once
    // BOOTSTRAP_SUCCESS has been processed and configureTradeIn() has had its chance to run.
    const isTradeReady = isBootstrapped && isTradeConfigured();
    const {
        changeVehicleComponent,
        dashboardComponent,
        dealHistoryComponent,
        creditBureauComponent,
        creditDecisionComponent,
        tradeComponent,
        accessoriesCatalogComponent,
        ddjComponent,
        auditLogComponent
    } = useDashComponentConfig({
        dealer,
        configurations: configs,
        isTradeReady
    });

    const enablePushToDarwin = featureToggleReducer.isPushToDarwinEnabled(toggles) && dealer.enableDarwin;
    const isGenerateContractsEnabled = useSelector(dealerSelectors.getEnableGenerateContracts);
    const isMVSalesTeamSlideoutEnabled = useSelector(featureToggleSelectors.isMVSalesTeamSlideoutEnabled);
    const isEnableMVCreditAppSearchAndMerge = useSelector(featureToggleSelectors.isEnableMVCreditAppSearchAndMerge);
    const isClearerSearchAndAttachExperienceEnabled = useSelector(featureToggleSelectors.isClearerSearchAndAttachExperienceEnabled);
    const isFuelSavingAnalysisEnabled = useSelector(featureToggleSelectors.isFuelSavingAnalysisEnabled);
    const isRoAlertEnabled = useSelector(featureToggleSelectors.isRoAlertInMVEnabled);
    const outOfBandBannerDisplayed = useSelector(multiScenarioSelectors.isOutOfBandBannerDisplayed);
    const isBaseDealer = useSelector(dealerSelectors.isBaseDealExperience);
    const saveDealStatus = useSelector(selectors.getSaveDealStatus);
    const isDDJEnabled = dealer.enableDigitalDealJacket;

    /**
     * Controls visibility of the UpdatedDealBanner based on two conditions:
     * Deal must be successfully saved (saveDealStatus === Idle)
     * and Open deal activity must be unmounted to prevent notifications while loading the dashboard.
     */

    const updateDealBanner = !outOfBandBannerDisplayed && saveDealStatus === ApiRequestStatus.Idle ? <UpdatedDealBanner /> : '';

    return (
        <>
            {isDDJEnabled && <DDJPreloader />}
            <NotificationContainer />
            <NavigationDrawerInterstate />
            <BodyContainer>
                <UnsavedChangesManager />
                {isGenerateContractsEnabled && <GenerateContract />}
                {isMVSalesTeamSlideoutEnabled && <EmployeeDetailSlideOut />}
                {isEnableMVCreditAppSearchAndMerge &&
                    (isClearerSearchAndAttachExperienceEnabled ? <CreditAppSearchSlideOutV2 /> : <CreditAppSearchSlideOut />)}
                {isFuelSavingAnalysisEnabled && <FuelSavingContainer />}
                {/* One instance per page, not per scenario card, so the RO link in a multi-scenario deal
                    opens a single drawer. */}
                {isRoAlertEnabled && <RepairOrdersSlideOut />}
                <DealHistoryDrawerContainer />
                <ConnectionStatusBanner />
                <DealTypeOverrideBanner />
                <BannerWrapper>{updateDealBanner}</BannerWrapper>
                {isBaseDealer && <PushToDMSSuccessBanner />}
                <BannerWrapper>
                    <OutOfBandUpdatedDealBanner />
                </BannerWrapper>
                <Banner />
                <Modal />
                <PendingReviewRetentionHost />
                <DealershipSelectionModal />
                <CreateDealModal />
                {dashboardComponent}
                {/* All routes, except the dashboard need to be inside this navigation scope,
                                            otherwise they will only affect the primary scope */}
                <ScopedStateProvider navigationScope>
                    {changeVehicleComponent}
                    {dealHistoryComponent}
                    {tradeComponent}
                    {creditBureauComponent}
                    {creditDecisionComponent}
                    {accessoriesCatalogComponent}
                    {ddjComponent}
                    {auditLogComponent}
                </ScopedStateProvider>
            </BodyContainer>
            <Footer />
            {enablePushToDarwin && <PushToDarwinPopup />}
            <DealHistoryScenarios />
        </>
    );
};
